import { useEffect, useRef } from 'react';
import isNumber from 'lodash/isNumber';
import { MEDIA_EVENT_LISTENER } from '../constants/video-events';
type PropsT = {
  videoElement: HTMLVideoElement | null;
  getFrameNumberFromTime: ((time: number) => number) | null;
  onLoad: () => void;
  onLoadedMetadata: (duration: number, el: HTMLVideoElement | null) => void;
  onDimensionChange: (w: number, h: number) => void;
  onFrameLoadStatus: (frameNumber: number) => void;
  onEndToLastFrame: () => void;
};
export function useVideoEventListeners({
  videoElement,
  getFrameNumberFromTime,
  onLoad,
  onLoadedMetadata,
  onDimensionChange,
  onEndToLastFrame,
  onFrameLoadStatus
}: PropsT) {
  const getFrameNumberFromTimeRef = useRef(getFrameNumberFromTime);
  // eslint-disable-next-line react-hooks/refs
  getFrameNumberFromTimeRef.current = getFrameNumberFromTime;
  const onLoadRef = useRef(onLoad);
  // eslint-disable-next-line react-hooks/refs
  onLoadRef.current = onLoad;
  const onLoadedMetadataRef = useRef(onLoadedMetadata);
  // eslint-disable-next-line react-hooks/refs
  onLoadedMetadataRef.current = onLoadedMetadata;
  const onDimensionChangeRef = useRef(onDimensionChange);
  // eslint-disable-next-line react-hooks/refs
  onDimensionChangeRef.current = onDimensionChange;
  const onFrameLoadStatusRef = useRef(onFrameLoadStatus);
  // eslint-disable-next-line react-hooks/refs
  onFrameLoadStatusRef.current = onFrameLoadStatus;
  const onEndToLastFrameRef = useRef(onEndToLastFrame);
  // eslint-disable-next-line react-hooks/refs
  onEndToLastFrameRef.current = onEndToLastFrame;
  useEffect(() => {
    if (!videoElement) {
      return;
    }
    const handleMediaMetadata = (e: Event) => {
      const target = (e.target as HTMLVideoElement | null);
      if (!target) {
        return;
      }
      onLoadRef.current();
      onLoadedMetadataRef.current(target.duration, videoElement);
      onDimensionChangeRef.current(target.videoWidth, target.videoHeight);
    };
    const handleMediaDownloadStatus = (e: Event) => {
      const target = (e.target as HTMLVideoElement | null);
      if (!target) {
        return;
      }
      if (!getFrameNumberFromTimeRef.current) {
        return;
      }
      const frameNumber = getFrameNumberFromTimeRef.current(target.currentTime);
      if (!isNumber(frameNumber)) {
        return;
      }
      onFrameLoadStatusRef.current(frameNumber);
    };
    const handleVideoEnd = (e: Event) => {
      const target = (e.target as HTMLVideoElement | null);
      if (!target) {
        return;
      }
      const isPlaying = !target.paused;
      if (!isPlaying) {
        return;
      }
      onEndToLastFrameRef.current();
    };
    videoElement.addEventListener(MEDIA_EVENT_LISTENER.LOADED_METADATA, handleMediaMetadata);
    videoElement.addEventListener(MEDIA_EVENT_LISTENER.PROGRESS, handleMediaDownloadStatus);
    videoElement.addEventListener(MEDIA_EVENT_LISTENER.SEEKED, handleMediaDownloadStatus);
    videoElement.addEventListener(MEDIA_EVENT_LISTENER.ENDED, handleVideoEnd);
    return () => {
      videoElement.removeEventListener(MEDIA_EVENT_LISTENER.LOADED_METADATA, handleMediaMetadata);
      videoElement.removeEventListener(MEDIA_EVENT_LISTENER.PROGRESS, handleMediaDownloadStatus);
      videoElement.removeEventListener(MEDIA_EVENT_LISTENER.SEEKED, handleMediaDownloadStatus);
      videoElement.removeEventListener(MEDIA_EVENT_LISTENER.ENDED, handleVideoEnd);
    };
  }, [videoElement]);
}